import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Portal } from "@radix-ui/react-tooltip";
import { RiCloseLargeLine, RiLink } from "@remixicon/react";
import { withCacheBuster } from "@/components/common/adCard/Media";

export interface CreativeAttachmentCardProps {
  name: string;
  thumbnailUrl?: string;
  mediaUrl?: string; // URL for fullscreen preview (if different from thumbnail)
  type: "image" | "video" | "mixed";
  metrics?: { id: string; label: string }[];
  showRemoveButton?: boolean;
  onRemove?: () => void;
  className?: string;
}

const CreativeAttachmentCard: React.FC<CreativeAttachmentCardProps> = ({
  name,
  thumbnailUrl,
  mediaUrl,
  type,
  metrics = [],
  showRemoveButton = false,
  onRemove,
  className,
}) => {
  const [preview, setPreview] = useState<{ url: string; layoutId: string } | null>(null);
  const [layoutTrigger, setLayoutTrigger] = useState(0);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
  const layoutId = useMemo(() => uuidv4(), []);

  const previewUrl = mediaUrl || thumbnailUrl;
  const isVideo = type === "video" && previewUrl;

  const handleThumbnailClick = () => {
    if (previewUrl) {
      setLayoutTrigger(Date.now());
      setPreview({ url: previewUrl, layoutId });
      setIsPreviewLoaded(false);
    }
  };

  const handleClosePreview = () => {
    setLayoutTrigger(Date.now());
    setPreview(null);
    setIsPreviewLoaded(false);
  };

  return (
    <>
      <TooltipProvider>
        <div
          className={`flex-shrink-0 w-fit max-w-[300px] h-[50px] relative group/creative flex items-center gap-2 backdrop-blur-sm rounded-[8px] bg-brand-orange bg-opacity-[0.02] border border-[#FF4500]/20 ${className || ""}`}
        >
          <div className="h-full relative shrink-0">
            {thumbnailUrl ? (
              <motion.img
                src={thumbnailUrl}
                alt={name}
                className="h-full aspect-square rounded-l-[8px] object-cover cursor-pointer"
                layoutId={layoutId}
                layoutDependency={layoutTrigger}
                onClick={handleThumbnailClick}
              />
            ) : (
              <div className="h-full aspect-square rounded-l-[8px] bg-gray-200/80 flex items-center justify-center">
                <span className="text-[10px] text-gray-500">
                  {type === "video" ? "🎬" : "🖼️"}
                </span>
              </div>
            )}
            {showRemoveButton && onRemove && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove();
                    }}
                    className="cursor-pointer absolute inset-0 flex items-center justify-center bg-black rounded-l-[8px] bg-opacity-0 opacity-0 group-hover/creative:opacity-100 group-hover/creative:bg-opacity-40 hover:opacity-100 hover:!bg-opacity-60 transition-opacity z-10"
                  >
                    <RiCloseLargeLine size={16} className="shrink-0 text-white" />
                  </button>
                </TooltipTrigger>
                <Portal>
                  <TooltipContent side="bottom" align="start">
                    <p>Remove creative</p>
                  </TooltipContent>
                </Portal>
              </Tooltip>
            )}
          </div>
          <div className="pr-[10px] py-2 flex flex-col min-w-0 flex-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-[12px] font-medium text-brand-primary-800 truncate">
                  {name}
                </span>
              </TooltipTrigger>
              <Portal>
                <TooltipContent side="bottom" align="start" className="max-w-[250px]">
                  <p>{name}</p>
                </TooltipContent>
              </Portal>
            </Tooltip>
            {metrics.length > 0 ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 text-[11px] text-brand-primary-600">
                    <RiLink size={14} className="shrink-0 text-brand-primary-400" />
                    <span className="truncate">
                      {metrics.map((m) => m.label).join(", ")}
                    </span>
                  </div>
                </TooltipTrigger>
                <Portal>
                  <TooltipContent side="bottom" align="start" className="max-w-[250px]">
                    <p>
                      {metrics.map((m) => m.label).join(", ")} are linked to this creative
                      using the section's date range and all-time data.
                    </p>
                  </TooltipContent>
                </Portal>
              </Tooltip>
            ) : (
              <div className="flex items-center gap-1 text-[11px] text-brand-primary-600">
                <RiLink size={14} className="shrink-0 text-brand-primary-400" />
                <span>No metrics here</span>
              </div>
            )}
          </div>
        </div>
      </TooltipProvider>

      {/* Fullscreen Preview - Rendered via Portal to document.body */}
      {createPortal(
        <AnimatePresence>
          {preview && (
            <>
              {/* Overlay */}
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClosePreview();
                }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
                style={{ pointerEvents: "auto" }}
              />

              {/* Enlarged Preview */}
              <motion.div
                key="previewWrapper"
                className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
              >
                <motion.div
                  className="w-fit relative pointer-events-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {/* Name overlay */}
                  {preview && isPreviewLoaded && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="absolute flex top-0 rounded-t-[6px] bg-gradient-to-b from-black/50 to-black/0 items-start h-16 px-3 py-2 w-full z-50"
                    >
                      <motion.span className="text-white text-[12px] font-normal leading-[14px] tracking-[-0.1px] truncate overflow-hidden whitespace-nowrap w-full block px-1 py-1">
                        {name}
                      </motion.span>
                    </motion.div>
                  )}
                  {isVideo ? (
                    <motion.video
                      src={preview.url}
                      layoutId={preview.layoutId}
                      className="max-h-[80vh] max-w-[80vw] object-contain rounded-md pointer-events-auto"
                      controls
                      onLoadedData={() => setTimeout(() => setIsPreviewLoaded(true), 400)}
                      onLoadStart={() => setIsPreviewLoaded(false)}
                    />
                  ) : (
                    <motion.img
                      src={withCacheBuster(preview.url)}
                      layoutId={preview.layoutId}
                      className="max-h-[80vh] max-w-[80vw] object-contain rounded-md pointer-events-auto"
                      onLoadStart={() => setIsPreviewLoaded(false)}
                      onLoad={() => setTimeout(() => setIsPreviewLoaded(true), 400)}
                      alt={name}
                    />
                  )}
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default CreativeAttachmentCard;
